// src/client/ApiClient.ts
import axios from "axios";
import { APP_BUILD_HEADER, RELOAD_REQUIRED_HEADER } from "@maat/api-shared";
var API_URL = "";
var apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json"
  }
});
var authTokenGetter;
var appBuildGetter;
var streamFetch = ((input, init) => fetch(input, init));
var userAgentOverride;
var SESSION_CLIENT_HEADER = "x-maat-client";
var sessionClient;
var sessionSink;
var refreshTokenGetter;
var sessionPreparer;
var REFRESH_URL = "/main/api/auth/refresh";
var LOGOUT_URL = "/main/api/auth/logout";
var configureApiClient = (config) => {
  if (config.baseURL !== void 0) {
    apiClient.defaults.baseURL = config.baseURL;
  }
  if (config.getAuthToken) {
    authTokenGetter = config.getAuthToken;
  }
  if (config.getAppBuild) {
    appBuildGetter = config.getAppBuild;
  }
  if (config.streamFetch) {
    streamFetch = config.streamFetch;
  }
  if (config.userAgent) {
    userAgentOverride = config.userAgent;
  }
  if (config.sessionClient) {
    sessionClient = config.sessionClient;
  }
  if (config.onSession) {
    sessionSink = config.onSession;
  }
  if (config.getRefreshToken) {
    refreshTokenGetter = config.getRefreshToken;
  }
  if (config.prepareSession) {
    sessionPreparer = config.prepareSession;
  }
};
var setAppBuildGetter = (getter) => {
  appBuildGetter = getter;
};
var getApiBaseUrl = () => apiClient.defaults.baseURL ?? "";
var getAuthHeaders = () => {
  const token = authTokenGetter?.();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
var getStreamFetch = () => streamFetch;
apiClient.interceptors.request.use(async (config) => {
  if (sessionPreparer && config.url !== REFRESH_URL && await sessionPreparer()) {
    await refreshSession().catch(() => void 0);
  }
  if (refreshTokenGetter && config.url === LOGOUT_URL && !config.data) {
    const refreshToken = refreshTokenGetter();
    if (refreshToken) config.data = { refresh_token: refreshToken };
  }
  const token = authTokenGetter?.();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  const build = appBuildGetter?.();
  if (build !== void 0) {
    config.headers.set(APP_BUILD_HEADER, String(build));
  }
  if (userAgentOverride) {
    config.headers.set("User-Agent", userAgentOverride);
  }
  if (sessionClient) {
    config.headers.set(SESSION_CLIENT_HEADER, sessionClient);
  }
  return config;
});
var unauthorizedHandler;
var setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler;
};
var reloadRequiredHandler;
var setReloadRequiredHandler = (handler) => {
  reloadRequiredHandler = handler;
};
var sessionProbe;
var setSessionProbe = (probe) => {
  sessionProbe = probe;
};
var AUTH_SKIP = /\/auth\/(refresh|login|register|oauth|logout|state|admin\/me|admin\/refresh)|\/auth\/account-deletion\/restore$|\/users\/me\/account-deletion$/;
var refreshInFlight = null;
var refreshSession = () => {
  if (!refreshInFlight) {
    const refreshToken = refreshTokenGetter?.();
    refreshInFlight = apiClient.post(REFRESH_URL, refreshToken ? { refresh_token: refreshToken } : void 0).catch(async (error) => {
      if (error?.response?.status === 401) await sessionSink?.(null);
      throw error;
    }).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};
apiClient.interceptors.response.use(
  async (response) => {
    if (response.headers?.[RELOAD_REQUIRED_HEADER] === "true") {
      reloadRequiredHandler?.();
    }
    if (sessionSink && response.config?.url === LOGOUT_URL) {
      await sessionSink(null);
    }
    const data = response.data;
    if (sessionSink && data && typeof data === "object" && "session" in data) {
      const { session, ...rest } = data;
      await sessionSink(session);
      return rest;
    }
    return data;
  },
  async (error) => {
    if (error.response?.headers?.[RELOAD_REQUIRED_HEADER] === "true") {
      reloadRequiredHandler?.();
    }
    const original = error.config;
    const status = error.response?.status;
    if (status === 401 && original && !original._retry && !AUTH_SKIP.test(original.url ?? "")) {
      const hasSession = sessionProbe ? sessionProbe() : authTokenGetter ? !!authTokenGetter() : true;
      if (!hasSession) {
        return Promise.reject(error);
      }
      original._retry = true;
      try {
        await refreshSession();
        return await apiClient(original);
      } catch {
        unauthorizedHandler?.();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export {
  apiClient,
  configureApiClient,
  setAppBuildGetter,
  getApiBaseUrl,
  getAuthHeaders,
  getStreamFetch,
  setUnauthorizedHandler,
  setReloadRequiredHandler,
  setSessionProbe,
  refreshSession
};
